import React, {
  ReactElement, useEffect, useLayoutEffect, useState,
} from 'react';
import { Route, Routes } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router';
import {
  getTitle, NotFoundPage, PageLoader, waitForElement,
} from 'slovlex-idsk-react-components';
import { useModalDialogueContext } from 'idsk-react-components';
import routes from './routes';
import {
  DolezitostPage,
  DolozkaVplyvovCreatePage,
  DolozkaVplyvovEditPage,
  DolozkaZlucitelnostiCreatePage,
  DolozkaZlucitelnostiEditPage,
  KategoriaAktEuPage,
  LegislativnyMaterialCreatePage,
  LegislativnyMaterialDetailPage,
  LegislativnyMaterialListPage,
  LegislativnyMaterialUpdatePage,
  MojeUlohyListPage,
  MpkAdminCancelPage,
  MpkAdminStartPage,
  MpkCancelPage,
  MpkConfigPage,
  MpkStartPage,
  NamietkaKuGestorstvuPage,
  NavrhUzneseniaVladyCreatePage,
  NavrhUzneseniaVladyEditPage,
  NotifikaciaHistoriaListPage,
  ObalMaterialuNrSrBezchybnostCreatePage,
  ObalMaterialuNrSrBezchybnostEditPage,
  ObalMaterialuNrSrCreatePage,
  ObalMaterialuNrSrEditPage,
  ObalMaterialuSelectPage,
  OdmietnutieGestoraPage,
  PodpisPredsedaPage,
  PredbeznaInformaciaListPage,
  PredkladatelUpdatePage,
  ProcessTerminatePage,
  RokovaniePovPage,
  SledovanyLegislativnyMaterialListPage,
  SpravaoUcastiVerejnostiCreatePage,
  SpravaoUcastiVerejnostiEditPage,
  SprievodnyDokumentListPage,
  SprievodnyDokumentUpdateListPage,
  SprievodnyDokumentZmenaPoradiaPage,
  UlohyOrganizacieDetailPage,
  UlohyOrganizacieListPage,
  UrcenieGestoraPage,
  UznesenieVladyCreatePage,
  UznesenieVladyEditPage,
  VseobecnyObalMaterialuCreatePage,
  VseobecnyObalMaterialuEditPage,
  VyhlasenievZbierkeZakonovPage,
  VyjadreniaAdminStartPage,
  VyjadreniaConfigPage,
  VyjadreniaPredbeznaInformaciaPage,
  ZmenaGestoraPage,
  ZmenaStadiaPage,
  PrechodZapisDoplnenehoStanoviskaPage,
} from '../page';
import handleAbsoluteUrlRedirects from './absoluteUrlHandler';
import { useSecurityContext } from '../context/SecurityContext';
import useFetchProfile from '../hooks/useFetchProfile';
import Layout from '../page/Layout';
import { useUserContext } from '../context/UserContext';

const ElegislativaRouter = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const [parentIsSettingRoute, setParentIsSettingRoute] = useState(true);
  const [anchorIsSettingRoute, setAnchorIsSettingRoute] = useState(true);

  const {
    setAccessToken,
    setOrganizationId,
    accessToken,
    organizationId,
  } = useSecurityContext();
  const { pouzivatel } = useUserContext();
  const { visible } = useModalDialogueContext();
  useFetchProfile();

  useEffect(() => {
    window.parent.postMessage({
      type: `iframe-to-parent-modal-dialogue-${visible ? 'open' : 'close'}`,
    }, '*');
  }, [visible]);

  const onMessage = (event): void => {
    const message = event?.data;
    if (message) {
      if (message.type && message.type === 'parent-to-iframe-internal-route') {
        setParentIsSettingRoute(true);
        navigate(message.route, { replace: true });
      } else if (message.type && message.type === 'pageYOffset') {
        const newHeight = message.portalMainWindowInnerHeight * (2 / 3);
        const modalDialogueWindow = document.querySelector<HTMLElement>('.modal-dialogue-window');
        const iframe = document.querySelector<HTMLElement>('#app');
        if (modalDialogueWindow && iframe) {
          if (iframe.offsetHeight > newHeight) {
            modalDialogueWindow.style.maxHeight = `${newHeight}px`;
          } else {
            modalDialogueWindow.style.maxHeight = `${iframe.offsetHeight - 10}px`;
          }
        }
        const warning = document.getElementsByClassName('sticky-element');
        if (warning && warning.length > 0) {
          const position = window.getComputedStyle(warning[0]).getPropertyValue('position');
          if (position !== 'relative') {
            const offset = warning[0].getAttribute('data-offset');
            const sticky = parseInt(offset, 10);
            if (message.pageYOffset > sticky && (position === 'fixed' || position === 'sticky')) {
              warning[0].setAttribute(
                'style',
                `position: fixed; top:${message.pageYOffset}px; width: 100%; background-color: white; z-index: 500`,
              );
            } else {
              warning[0].setAttribute('style', 'position: sticky;');
            }
          }
        }
      } else if (typeof message === 'string' && message.includes('accessToken=')) {
        setAccessToken(message.substring('accessToken='.length));
      } else if (typeof message === 'string' && message.includes('organizationId=')) {
        setOrganizationId(message.substring('organizationId='.length));
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', onMessage);
    window.addEventListener('click', () => {
      window.parent.postMessage({ type: 'iframe-to-parent-clicked' }, '*');
    });
    window.parent.postMessage({ type: 'iframe-to-parent-initialized' }, '*');
    handleAbsoluteUrlRedirects();
    if (window.self === window.top) {
      window.location.replace(`${process.env.REACT_APP_PORTAL_MAIN_URL}/elegislativa`
        + `${location.pathname ? location.pathname : ''}`
        + `${location.search ? location.search : ''}${location.hash ? location.hash : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (document.referrer && document.referrer !== document.location.href
      && location.pathname !== routes.loading.to
      && parentIsSettingRoute && anchorIsSettingRoute) {
      notifyParent();
      setAnchorIsSettingRoute(false);
    }

    if (parentIsSettingRoute && anchorIsSettingRoute) {
      setParentIsSettingRoute(false);
      return;
    }
    notifyParent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, location.search, location.state]);

  const notifyParent = (): void => {
    waitForElement('#app > .elegislativa-main-container')
      .then(() => {
        window.parent.postMessage({
          type: 'iframe-to-parent-internal-route',
          route: `/elegislativa${location.pathname ? location.pathname : ''
          }${location.search ? location.search : ''
          }${location.hash ? location.hash : ''}`,
          title: getTitle(),
        }, '*');
      });
  };

  if (accessToken === undefined || organizationId === undefined) {
    return (
      <div className="page-loader">
        <PageLoader loading />
      </div>
    );
  }

  if (accessToken && pouzivatel === null) {
    return (
      <div className="page-loader">
        <PageLoader loading />
      </div>
    );
  }

  return (
    <Routes>
      <Route path={routes.loading.to} element={null} />
      <Route
        path={routes.procesy.list}
        element={<LegislativnyMaterialListPage />}
      />
      <Route
        path={routes.procesy.predbeznaInformacia.list}
        element={<PredbeznaInformaciaListPage />}
      />
      <Route
        path={routes.procesy.sledovane}
        element={<SledovanyLegislativnyMaterialListPage />}
      />
      <Route
        path={routes.mojeUlohy}
        element={<MojeUlohyListPage />}
      />
      <Route
        path={routes.ulohyOrganizacie.list}
        element={<UlohyOrganizacieListPage />}
      />
      <Route
        path={routes.ulohyOrganizacie.detail.raw}
        element={<UlohyOrganizacieDetailPage />}
      />
      <Route
        path={routes.procesy.new}
        element={<LegislativnyMaterialCreatePage />}
      />
      <Route
        path={routes.procesy.edit.raw}
        element={<LegislativnyMaterialUpdatePage />}
      />
      <Route
        path={routes.procesy.detail.raw}
        element={<LegislativnyMaterialDetailPage />}
      />
      <Route
        path={routes.procesy.terminate.raw}
        element={<ProcessTerminatePage />}
      />
      <Route
        path={routes.procesy.vyjadrenia.raw}
        element={<VyjadreniaPredbeznaInformaciaPage />}
      />
      <Route
        path={routes.procesy.admin.vyjadrenia.start.raw}
        element={<VyjadreniaAdminStartPage />}
      />
      <Route
        path={routes.procesy.admin.vyjadrenia.config.raw}
        element={<VyjadreniaConfigPage />}
      />
      <Route
        path={routes.procesy.mpk.raw}
        element={<MpkStartPage />}
      />
      <Route
        path={routes.procesy.admin.mpk.config.raw}
        element={<MpkConfigPage />}
      />
      <Route
        path={routes.procesy.admin.mpk.start.raw}
        element={<MpkAdminStartPage />}
      />
      <Route
        path={routes.procesy.mpk.cancel.raw}
        element={<MpkCancelPage />}
      />
      <Route
        path={routes.procesy.admin.mpk.cancel.raw}
        element={<MpkAdminCancelPage />}
      />
      <Route
        path={routes.procesy.rokovanie.raw}
        element={<RokovaniePovPage />}
      />
      <Route
        path={routes.procesy.dolezitost.raw}
        element={<DolezitostPage />}
      />
      <Route
        path={routes.procesy.gestor.raw}
        element={<UrcenieGestoraPage />}
      />
      <Route
        path={routes.procesy.admin.gestor.raw}
        element={<ZmenaGestoraPage />}
      />
      <Route
        path={routes.procesy.admin.notifikacie.raw}
        element={<NotifikaciaHistoriaListPage />}
      />
      <Route
        path={routes.procesy.gestor.odmietnutie.raw}
        element={<OdmietnutieGestoraPage />}
      />
      <Route
        path={routes.procesy.kategoria.raw}
        element={<KategoriaAktEuPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.list.raw}
        element={<SprievodnyDokumentListPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.edit.raw}
        element={<SprievodnyDokumentUpdateListPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.editAsAdmin.raw}
        element={<SprievodnyDokumentUpdateListPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.editOrder.raw}
        element={<SprievodnyDokumentZmenaPoradiaPage />}
      />
      <Route
        path={routes.procesy.predkladatel.edit.raw}
        element={<PredkladatelUpdatePage />}
      />
      <Route
        path={routes.procesy.namietkaKuGestorstvu.raw}
        element={<NamietkaKuGestorstvuPage />}
      />
      <Route
        path={routes.procesy.podpisPredseda.raw}
        element={<PodpisPredsedaPage />}
      />
      <Route
        path={routes.procesy.prechodZapisStanoviska.raw}
        element={<PrechodZapisDoplnenehoStanoviskaPage />}
      />
      <Route
        path={routes.procesy.podpisPredseda.obalMaterialu.create.raw}
        element={<ObalMaterialuNrSrBezchybnostCreatePage />}
      />
      <Route
        path={routes.procesy.podpisPredseda.obalMaterialu.edit.raw}
        element={<ObalMaterialuNrSrBezchybnostEditPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.dolozkaVplyvov.create.raw}
        element={<DolozkaVplyvovCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.dolozkaVplyvov.edit.raw}
        element={<DolozkaVplyvovEditPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.dolozkaZlucitelnosti.create.raw}
        element={<DolozkaZlucitelnostiCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.dolozkaZlucitelnosti.edit.raw}
        element={<DolozkaZlucitelnostiEditPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.spravaOUcastiVerejnosti.create.raw}
        element={<SpravaoUcastiVerejnostiCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.spravaOUcastiVerejnosti.edit.raw}
        element={<SpravaoUcastiVerejnostiEditPage />}
      />
      <Route
        path={routes.procesy.vyhlasenievZbierke.raw}
        element={<VyhlasenievZbierkeZakonovPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.navrhUzneseniaVlady.create.raw}
        element={<NavrhUzneseniaVladyCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.navrhUzneseniaVlady.edit.raw}
        element={<NavrhUzneseniaVladyEditPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.uznesenieVlady.create.raw}
        element={<UznesenieVladyCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.uznesenieVlady.edit.raw}
        element={<UznesenieVladyEditPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.obalMaterialu.select.raw}
        element={<ObalMaterialuSelectPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.obalMaterialu.vseobecny.create.raw}
        element={<VseobecnyObalMaterialuCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.obalMaterialu.obalNrSr.create.raw}
        element={<ObalMaterialuNrSrCreatePage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.obalMaterialu.vseobecny.edit.raw}
        element={<VseobecnyObalMaterialuEditPage />}
      />
      <Route
        path={routes.procesy.detail.sprievodneDokumenty.obalMaterialu.obalNrSr.edit.raw}
        element={<ObalMaterialuNrSrEditPage />}
      />
      <Route
        path={routes.procesy.admin.zmenaStadiaMaterialu.raw}
        element={<ZmenaStadiaPage />}
      />
      <Route
        path={routes.notFound}
        element={(
          <Layout>
            <NotFoundPage />
          </Layout>
        )}
      />
      <Route
        path="*"
        element={(
          <Layout>
            <NotFoundPage />
          </Layout>
        )}
      />
    </Routes>
  );
};

export default ElegislativaRouter;
